import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Store, MapPin, Phone, Globe, Mail, Send, CheckCircle,
  Utensils, ShoppingBag, Wrench, Heart, Briefcase, Car, Scissors, Home, Navigation, ExternalLink
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'Restaurant', labelKey: 'business.categories.restaurant', icon: Utensils },
  { value: 'Retail', labelKey: 'business.categories.retail', icon: ShoppingBag },
  { value: 'Services', labelKey: 'business.categories.services', icon: Briefcase },
  { value: 'Health', labelKey: 'business.categories.health', icon: Heart },
  { value: 'Auto', labelKey: 'business.categories.auto', icon: Car },
  { value: 'Beauty', labelKey: 'business.categories.beauty', icon: Scissors },
  { value: 'Home', labelKey: 'business.categories.home', icon: Home },
  { value: 'Other', labelKey: 'business.categories.other', icon: Store }
];

// Default featured businesses
const defaultBusinesses = [
  {
    id: 'default-1',
    business_name: 'Smallest Souls Animal Rescue',
    description: '501(c)(3) nonprofit organization dedicated to rescuing, rehabilitating, and rehoming animals in need. Every soul matters, no matter how small.',
    category: 'Services',
    website: 'https://www.smallestsoulsanimalrescue.com/',
    address: 'Kissimmee, FL',
    phone: '(954) 298-1951',
    email: 'smallestsouls@gmail.com'
  },
  {
    id: 'default-2',
    business_name: 'Foamblasterz',
    description: 'Fun and exciting foam parties and entertainment for all ages. Perfect for birthdays, community events, and celebrations!',
    category: 'Services',
    website: 'https://foamblasterz.com/',
    address: 'Central Florida',
    phone: '',
    email: ''
  },
  {
    id: 'default-3',
    business_name: 'Odin Delgado - Heritage International Realty',
    description: 'Trusted realtor helping families find their dream homes in Central Florida. Dedicated to providing exceptional service for all your real estate needs.',
    category: 'Services',
    website: '',
    address: 'Central Florida',
    phone: '(407) 954-2498',
    phone2: '(407) 613-9157',
    email: ''
  },
  {
    id: 'default-4',
    business_name: 'Joanne Walker - Smart International Title',
    description: 'Title Closer/Notary & Customer Service Specialist with half a century of unparalleled customer service. Meticulous attention to every detail of the title closing process.',
    category: 'Services',
    website: 'https://smartinternationaltitle.com/joanne/',
    address: '2008 N Goldenrod Rd, Orlando, FL 32807',
    phone: '(407) 854-2115',
    email: 'SmartClosings@SmartInternationalTitle.com'
  }
];

export default function LocalBusinessPage() {
  const { t } = useTranslation();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    category: '',
    description: '',
    logo: ''
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/features/local-businesses?approved_only=true`);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error(t('business.selectCategory', 'Please select a category'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/features/local-businesses`, formData);
      setSubmitted(true);
      setFormData({
        business_name: '',
        owner_name: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        category: '',
        description: '',
        logo: ''
      });
      toast.success(t('business.submitted', 'Business submitted for review!'));
    } catch (error) {
      toast.error(t('common.error', 'Failed to submit. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : Store;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Restaurant': 'bg-orange-100 text-orange-600',
      'Retail': 'bg-blue-100 text-blue-600',
      'Services': 'bg-purple/10 text-purple',
      'Health': 'bg-pink-100 text-pink-600',
      'Auto': 'bg-gray-100 text-gray-600',
      'Beauty': 'bg-rose-100 text-rose-600',
      'Home': 'bg-olive/10 text-olive',
      'Other': 'bg-gold/10 text-gold'
    };
    return colors[category] || 'bg-gray-100 text-gray-600';
  };

  const getMapUrl = (address) => {
    const encoded = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  };

  const getDirectionsUrl = (address) => {
    const encoded = encodeURIComponent(address);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      return `https://maps.google.com/maps?daddr=${encoded}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  };

  const getEmbedMapUrl = (address) => {
    const encoded = encodeURIComponent(address);
    return `https://maps.google.com/maps?q=${encoded}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  };

  // Combine API businesses with default featured businesses
  const allBusinesses = [...defaultBusinesses, ...businesses];
  
  const filteredBusinesses = selectedCategory === 'all' 
    ? allBusinesses 
    : allBusinesses.filter(b => b.category === selectedCategory);

  return (
    <div className="min-h-screen bg-cream" data-testid="local-business-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-purple to-purple/90">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Store size={48} className="mx-auto text-gold mb-4" />
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              {t('business.title', 'Support Local')}
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              {t('business.subtitle', 'Discover and support local businesses in our community. Shop local, support your neighbors!')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Submit Your Business Banner */}
      <section className="bg-gold/20 border-b border-gold/30">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-charcoal font-medium text-lg">{t('business.ownBusiness', 'Own a Local Business?')}</p>
              <p className="text-charcoal/70">{t('business.getFeatured', "Get featured in our community directory - it's free!")}</p>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-burgundy hover:bg-purple text-white"
            >
              {showForm ? t('common.close', 'Close Form') : t('business.submitBusiness', 'Submit Your Business')}
            </Button>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      {showForm && (
        <section className="bg-white border-b border-gold/20">
          <div className="max-w-2xl mx-auto px-6 py-10">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
              >
                <CheckCircle size={64} className="mx-auto text-olive mb-4" />
                <h2 className="font-serif text-2xl text-charcoal mb-2">{t('common.success', 'Thank You!')}</h2>
                <p className="text-charcoal/70 mb-6">
                  {t('business.submittedMessage', "Your business has been submitted for review. We'll notify you once it's approved.")}
                </p>
                <Button onClick={() => { setSubmitted(false); setShowForm(false); }} variant="outline">
                  {t('common.close', 'Close')}
                </Button>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="text-center mb-8">
                  <h2 className="font-serif text-2xl text-charcoal">{t('business.submitBusiness', 'Submit Your Business')}</h2>
                  <p className="text-charcoal/60">{t('business.fillForm', 'Fill out the form below to be featured in our directory')}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('business.businessName', 'Business Name')} *</Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                      placeholder={t('business.businessNamePlaceholder', 'Your Business Name')}
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('business.ownerName', 'Owner Name')} *</Label>
                    <Input
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                      placeholder={t('business.ownerNamePlaceholder', 'Your Name')}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('common.email', 'Email')} *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('common.phone', 'Phone')}</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <Label>{t('business.category', 'Category')} *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('business.selectCategory', 'Select a category')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{t(cat.labelKey, cat.value)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{t('business.address', 'Business Address')}</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main St, Kissimmee, FL"
                  />
                </div>

                <div>
                  <Label>{t('business.website', 'Website')}</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://www.yourbusiness.com"
                  />
                </div>

                <div>
                  <Label>{t('business.logo', 'Logo URL (optional)')}</Label>
                  <Input
                    value={formData.logo}
                    onChange={(e) => setFormData({...formData, logo: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <Label>{t('business.description', 'Business Description')} *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={t('business.descriptionPlaceholder', 'Tell us about your business, what you offer, and why the community should support you...')}
                    rows={4}
                    required
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-burgundy hover:bg-purple">
                  <Send size={18} className="mr-2" />
                  {submitting ? t('common.loading', 'Submitting...') : t('business.submitForReview', 'Submit for Review')}
                </Button>
              </motion.form>
            )}
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="bg-white border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-charcoal/60 text-sm font-medium mr-2">{t('business.filter', 'Filter')}:</span>
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all' ? 'bg-burgundy text-white' : 'bg-cream text-charcoal hover:bg-gold/20'
              }`}
            >
              {t('business.all', 'All')}
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.value ? 'bg-burgundy text-white' : 'bg-cream text-charcoal hover:bg-gold/20'
                }`}
              >
                {t(cat.labelKey, cat.value)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Business Listings */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="text-center py-16">
              <Store size={64} className="mx-auto text-charcoal/20 mb-4" />
              <h2 className="font-serif text-2xl text-charcoal mb-2">{t('business.noBusinesses', 'No Businesses Yet')}</h2>
              <p className="text-charcoal/60 max-w-md mx-auto mb-6">
                {t('business.beFirst', 'Be the first to get your business featured in our community directory!')}
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-burgundy hover:bg-purple">
                {t('business.submitBusiness', 'Submit Your Business')}
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBusinesses.map((business, index) => {
                const CategoryIcon = getCategoryIcon(business.category);
                return (
                  <motion.div
                    key={business.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-white rounded-2xl overflow-hidden border border-gold/10 hover:border-gold/30 hover:shadow-lg transition-all ${business.is_featured ? 'ring-2 ring-gold' : ''}`}
                  >
                    {/* Map Preview */}
                    {business.address && (
                      <div className="relative h-32 bg-cream">
                        <iframe
                          src={getEmbedMapUrl(business.address)}
                          className="w-full h-full border-0"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`Map for ${business.business_name}`}
                        />
                        <a
                          href={getDirectionsUrl(business.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute bottom-2 right-2 bg-burgundy text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 hover:bg-purple transition-colors shadow-lg"
                        >
                          <Navigation size={12} /> {t('business.getDirections', 'Get Directions')}
                        </a>
                      </div>
                    )}
                    
                    <div className="p-6">
                      {business.is_featured && (
                        <span className="inline-block px-3 py-1 bg-gold text-white text-xs font-medium rounded-full mb-4">
                          {t('business.featured', 'Featured')}
                        </span>
                      )}
                      
                      <div className="flex items-start gap-4 mb-4">
                        {business.logo ? (
                          <img src={business.logo} alt={business.business_name} className="w-16 h-16 object-contain rounded-xl bg-cream p-2" />
                        ) : (
                          <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${getCategoryColor(business.category)}`}>
                            <CategoryIcon size={28} />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-serif text-lg text-charcoal">{business.business_name}</h3>
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${getCategoryColor(business.category)}`}>
                            {business.category}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-charcoal/70 text-sm mb-4 line-clamp-3">{business.description}</p>
                      
                      <div className="space-y-2 text-sm">
                        {business.address && (
                          <a 
                            href={getMapUrl(business.address)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-2 text-charcoal/60 hover:text-burgundy transition-colors"
                          >
                            <MapPin size={14} className="mt-0.5 flex-shrink-0" /> 
                            <span className="line-clamp-2">{business.address}</span>
                          </a>
                        )}
                        {business.phone && (
                          <p className="flex items-center gap-2 text-charcoal/60">
                            <Phone size={14} />
                            <a href={`tel:${business.phone}`} className="hover:text-burgundy">{business.phone}</a>
                          </p>
                        )}
                        {business.website && (
                          <a 
                            href={business.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-burgundy hover:underline"
                          >
                            <Globe size={14} /> {t('business.visitWebsite', 'Visit Website')} <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
